import React, {Component, ReactNode} from 'react';
import Breadcrumb from '../components/Breadcrumb';
import Icon from '../icons/Icon';
import {FormicaProps, withFormica} from '../helpers/withFormica';
import {dispatch, store} from '../redux/store';
import ComponentName from '../constant/ComponentName';
import LanguageUtil from '../util/LanguageUtil';
import MessageUtil from '../util/MessageUtil';
import ServiceContainer from '../config/ServiceContainer';
import {ServiceNames} from '../constant/ServiceNames';
import CustomListService from '../service/CustomListService';
import {
  CustomListModel,
  CustomListRecordDeleteAllRequest,
  CustomListRecordExportRequest,
  CustomListRecordFilter,
  CustomListRecordRowModel,
  CustomListTabType,
} from '../domain/custom-list/CustomListDomains';
import CustomListRecordTableComponent from '../components/kyc/custom-list/CustomListRecordTableComponent';
import CustomListCsvModal from '../components/kyc/custom-list/CustomListCsvModal';
import CustomListEmptyImportComponent from '../components/kyc/custom-list/CustomListEmptyImportComponent';
import CustomEmptyComponent from '../components/custom/CustomEmptyComponent';
import CustomLoading from '../components/custom/CustomLoading';
import RoleHelper from '../RoleHelper';
import {changeActivePageIndex, changePageFilter, changePagePath} from '../redux/actions/PageActions';
import {ConfirmPopup} from 'primereact/confirmpopup';
import CustomTabv2, {CustomTabItem} from '../components/custom/CustomTabv2';
import ItemToolsMenu from '../components/ItemToolsMenu';
import FilterUtil from '../util/FilterUtil';
import CustomListRouteUtil from '../util/CustomListRouteUtil';
import {
  CustomFilter as FilterType,
  CustomFilterCategorical,
  CustomFilterDateSearch,
  CustomFilterFields,
  CustomFilterLiteralSearch
} from '../domain/ui/FilterDomains';
import {connect, ConnectedProps} from 'react-redux';
import {setSelectedItems} from '../redux/actions/ComponentActions';
import {query} from '@trlogic/client-js';
import _ from 'lodash';
import moment from 'moment';
import DataType from '../constant/DataType';
import Filter = query.Filter;
import Order = query.Order;
import empty = query.empty;

interface QueryState {
  tab: CustomListTabType;
  currentPage: number;
  rows: number;
}

interface BulkBody {
  recordType: CustomListRecordDeleteAllRequest['recordType'];
  filter?: CustomListRecordFilter;
  ids?: string[];
}

class State {
  public list: CustomListModel | undefined = undefined;
  public records: CustomListRecordRowModel[] = [];
  public selectedRecords: CustomListRecordRowModel[] = [];
  public total: number = 0;
  public loading: boolean = true;
  public csvModalVisible: boolean = false;
  public tab: CustomListTabType = 'individual';
  public currentPage: number = 1;
  public rows: number = 10;
  public sorts: Order[] = [{field: 'createdAt', operator: 'DESC'}];
  public filter: Filter = empty();
  public uiFilter: FilterType | undefined = undefined;
}

class CustomListDetailContainer extends Component<Props, State> {
  public state: State = new State();

  private readonly customListService: CustomListService = ServiceContainer.getInstance().get(ServiceNames.CUSTOM_LIST);
  private skipNextSearchReload: boolean = false;

  public render(): ReactNode {
    const {list, csvModalVisible, tab} = this.state;
    const listId: string = this.props.router.params.listId ?? '';
    const title: string = list?.name ?? LanguageUtil.get({id: 'common.label.customList', defaultMessage: 'Custom List'});
    const description: string = list?.description?.trim() ?? '';

    return (
      <div className={'w-full h-full flex flex-col overflow-hidden'}>
        <Breadcrumb/>
        <ConfirmPopup/>
        <div className={'p-5 shrink-0'}>
          <div className={'flex'}>
            <span
              className="w-[60px] h-[60px] min-h-[60px] min-w-[60px] 2xl:w-[80px] 2xl:h-[80px] 2xl:min-h-[80px] 2xl:min-w-[80px] flex justify-center items-center rounded-[50%] bg-[#EFF2FE] mr-1">
              <Icon icon={'customlisticon'} className={'text-f-purple'} size={32}/>
            </span>
            <div className={'flex flex-col justify-center pl-3'}>
              <span className={'text-[24px] 2xl:text-4xl font-normal'}>{title}</span>
              {description.length > 0 && (
                <span className={'text-md font-normal !text-f-black-3'}>{description}</span>
              )}
            </div>
          </div>
        </div>
        <div className={'flex flex-col overflow-y-hidden min-h-0 h-full w-full'}>
          <CustomTabv2
            selectedTab={tab}
            onClick={this.onTabChange}
            isContainer={true}
            tab={this.getTabs(listId)}
          />
        </div>
        <CustomListCsvModal
          visible={csvModalVisible}
          listId={listId}
          recordType={tab}
          onHide={this.closeCsvModal}
          onSuccess={this.loadRecords}
        />
      </div>
    );
  }

  public async componentDidMount(): Promise<void> {
    if (this.redirectIfTabMissing()) {
      return;
    }
    const routeState: QueryState = this.parseRouteState();
    this.setState(routeState, async () => {
      await this.loadList();
      this.prepareFilterFields();
      await this.loadRecords();
    });
  }

  public async componentDidUpdate(prevProps: Props): Promise<void> {
    const previousListId: string | undefined = prevProps.router.params.listId;
    const currentListId: string | undefined = this.props.router.params.listId;
    const previousRecordType: string | undefined = prevProps.router.params.recordType;
    const currentRecordType: string | undefined = this.props.router.params.recordType;
    const previousSearch: string = prevProps.router.location.search;
    const currentSearch: string = this.props.router.location.search;

    if (this.redirectIfTabMissing()) {
      return;
    }

    if (previousListId !== currentListId) {
      const routeState: QueryState = this.parseRouteState();
      this.setState({...routeState, selectedRecords: [], records: [], total: 0, loading: true}, async () => {
        await this.loadList();
        this.prepareFilterFields();
        await this.loadRecords();
      });
      return;
    }

    if (previousRecordType !== currentRecordType) {
      const routeState: QueryState = this.parseRouteState();
      this.setState(
        {
          ...routeState,
          selectedRecords: [],
          records: [],
          total: 0,
          loading: true,
          sorts: [{field: 'createdAt', operator: 'DESC'}],
        },
        async () => {
          this.prepareFilterFields();
          await this.loadRecords();
        },
      );
      return;
    }

    if (previousSearch !== currentSearch) {
      if (this.skipNextSearchReload) {
        this.skipNextSearchReload = false;
        return;
      }
      const routeState: QueryState = this.parseRouteState();
      this.setState(routeState, () => {
        this.loadRecords();
      });
    }
  }

  private getTabs = (listId: string): CustomTabItem[] => {
    return [
      {
        key: 'individual',
        label: LanguageUtil.get({id: 'pages.customList.tab.individual', defaultMessage: 'Individual'}),
        content: this.renderTabContent('individual', listId),
      },
      {
        key: 'entity',
        label: LanguageUtil.get({id: 'pages.customList.tab.entity', defaultMessage: 'Entity'}),
        content: this.renderTabContent('entity', listId),
      },
    ];
  };

  private renderTabContent = (tabKey: CustomListTabType, listId: string): ReactNode => {
    const {tab, records, selectedRecords, loading, total, currentPage, rows, sorts, filter, uiFilter} = this.state;
    if (tab !== tabKey) {
      return null;
    }

    const hasRecords: boolean = total > 0 || records.length > 0;
    const hasActiveFilter: boolean = (uiFilter?.fields?.length ?? 0) > 0;
    const itemsIsSelected: boolean = selectedRecords.length > 0;
    const showEmptyImport: boolean = !loading && !hasRecords && !hasActiveFilter;

    return (
      <div className={'flex flex-col overflow-y-hidden min-h-0 h-full w-full px-5'}>
        <ItemToolsMenu
          views={false}
          countVisible={true}
          totalCount={total}
          importVisible={true}
          exportVisibleAlways={true}
          exportVisible={hasRecords || itemsIsSelected}
          itemsIsSelected={itemsIsSelected}
          componentLabel={LanguageUtil.get({
            id: tabKey === 'individual' ? 'pages.customList.tab.individual' : 'pages.customList.tab.entity',
            defaultMessage: tabKey === 'individual' ? 'Individual' : 'Entity',
          })}
          component={ComponentName.CUSTOM_LIST}
          componentId={`${listId}-${tabKey}`}
          importFunc={this.openCsvModal}
          exportFunc={this.onExport}
          removeAllFunc={this.onRemoveAll}
          onChangeFilter={this.onChangeFilter}
          filter={filter}
          defaultFilterDisabled={true}
        />
        {loading ? (
          <CustomLoading/>
        ) : hasRecords ? (
          <CustomListRecordTableComponent
            records={records}
            selectedRecords={selectedRecords}
            totalRecords={total}
            currentPage={currentPage}
            rows={rows}
            recordType={tabKey}
            sorts={sorts}
            onSelectionChange={this.onSelectionChange}
            onDelete={this.onDeleteRecord}
            onRowClick={this.onRowClick}
            onPageChange={this.onPageChange}
            onSortChange={this.onSortChange}
            onAddColumnFilter={this.onAddColumnFilter}
          />
        ) : showEmptyImport && RoleHelper.canEdit(ComponentName.CUSTOM_LIST) ? (
          <CustomListEmptyImportComponent
            listId={listId}
            recordType={tabKey}
            onSuccess={this.loadRecords}
          />
        ) : (
          <div className={'flex w-full h-full items-center justify-center'}>
            <CustomEmptyComponent
              label={
                hasActiveFilter
                  ? LanguageUtil.get({
                    id: 'pages.customList.emptyFilteredRecords',
                    defaultMessage: 'No records match the current filters.',
                  })
                  : LanguageUtil.get({
                    id: 'pages.customList.emptyRecords',
                    defaultMessage: 'No records yet.',
                  })
              }
            />
          </div>
        )}
      </div>
    );
  };

  private onTabChange = (_event: unknown, tabKey: string): void => {
    const tab: CustomListTabType = CustomListRouteUtil.resolveTab(tabKey);
    if (tab === this.state.tab) {
      return;
    }
    const listId: string = this.props.router.params.listId ?? '';
    this.setState(
      {
        tab,
        currentPage: 1,
        selectedRecords: [],
        records: [],
        total: 0,
        loading: true,
        sorts: [{field: 'createdAt', operator: 'DESC'}],
      },
      async () => {
        this.prepareFilterFields();
        this.skipNextSearchReload = true;
        this.navigateToTabPath(listId, tab, 1, this.state.rows);
        await this.loadRecords();
      },
    );
  };

  private openCsvModal = (): void => {
    this.setState({csvModalVisible: true});
  };

  private closeCsvModal = (): void => {
    this.setState({csvModalVisible: false});
  };

  private buildBulkBody = (): BulkBody => {
    const {selectedRecords, tab, uiFilter} = this.state;
    const ids: string[] | undefined = selectedRecords.length > 0
      ? selectedRecords.map((record) => record.id)
      : undefined;
    return {
      recordType: this.customListService.toDbRecordType(tab),
      filter: ids == undefined ? this.customListService.mapUiFilterToRequest(uiFilter) : undefined,
      ids,
    };
  };

  private onExport = async (): Promise<void> => {
    const listId: string = this.props.router.params.listId;
    if (!listId) {
      return;
    }

    const {sorts} = this.state;
    const body: BulkBody = this.buildBulkBody();
    const request: CustomListRecordExportRequest = {
      ...body,
      sort: sorts[0]
        ? {field: sorts[0].field, order: sorts[0].operator === 'ASC' ? 'ASC' : 'DESC'}
        : undefined,
    };

    try {
      await this.customListService.exportRecords(listId, request);
    } catch (e) {
      console.error(e);
      MessageUtil.error({
        summary: LanguageUtil.get({
          id: 'pages.customList.exportFailed',
          defaultMessage: 'Failed to export records',
        }),
      });
    }
  };

  private onSelectionChange = (records: CustomListRecordRowModel[]): void => {
    this.setState({selectedRecords: records});
    dispatch(setSelectedItems(records.map((record) => ({id: record.id, stage: 'DRAFT'}))));
  };

  private onRemoveAll = async (): Promise<void> => {
    const {selectedRecords} = this.state;

    const listId: string = this.props.router.params.listId;
    if (!listId) {
      return;
    }

    if (selectedRecords.length === 0) {
      return;
    }

    const request: CustomListRecordDeleteAllRequest = this.buildBulkBody();

    try {
      await this.customListService.deleteRecords(listId, request);
      MessageUtil.success({
        summary: LanguageUtil.get({
          id: 'pages.customList.recordDeleteSuccess',
          defaultMessage: 'Record deleted',
        }),
      });
      this.setState({selectedRecords: []});
      dispatch(setSelectedItems([]));
      await this.loadRecords();
    } catch (e) {
      console.error(e);
      MessageUtil.error({
        summary: LanguageUtil.get({
          id: 'pages.customList.recordDeleteFailed',
          defaultMessage: 'Failed to delete record',
        }),
      });
    }
  };

  private onDeleteRecord = async (record: CustomListRecordRowModel): Promise<void> => {
    const listId: string = this.props.router.params.listId;
    if (!listId) {
      return;
    }

    try {
      await this.customListService.deleteRecord(listId, record.id);
      MessageUtil.success({
        summary: LanguageUtil.get({
          id: 'pages.customList.recordDeleteSuccess',
          defaultMessage: 'Record deleted',
        }),
      });
      await this.loadRecords();
    } catch (e) {
      console.error(e);
      MessageUtil.error({
        summary: LanguageUtil.get({
          id: 'pages.customList.recordDeleteFailed',
          defaultMessage: 'Failed to delete record',
        }),
      });
    }
  };

  private onRowClick = (record: CustomListRecordRowModel): void => {
    const listId: string = this.props.router.params.listId;
    if (!listId) {
      return;
    }
    this.props.router.navigate(`/custom-list/${listId}/${this.state.tab}/detail/${record.id}`);
  };

  private onPageChange = (page: number, rows: number): void => {
    this.setState({currentPage: page, rows}, async () => {
      this.skipNextSearchReload = true;
      this.writePaginationParams();
      await this.loadRecords();
    });
  };

  private onSortChange = (sorts: Order[]): void => {
    this.setState({sorts, currentPage: 1}, async () => {
      this.skipNextSearchReload = true;
      this.writePaginationParams();
      await this.loadRecords();
    });
  };

  private onChangeFilter = (filter: Filter): void => {
    if (_.isEqual(filter, this.state.filter)) {
      return;
    }
    const uiFilter: FilterType = store.getState().page.filter.filter;
    this.setState({filter, uiFilter, currentPage: 1, selectedRecords: [], loading: true}, async () => {
      this.skipNextSearchReload = true;
      this.writePaginationParams();
      await this.loadRecords();
    });
  };

  private onAddColumnFilter = (
    field: string,
    value: string | number,
    label: string,
    dataType: DataType,
    categorical: boolean = false,
  ): void => {
    const {changePageFilter} = this.props;
    const filterState = store.getState().page.filter;

    if (categorical) {
      filterState.filter.fields.push({
        field,
        label,
        enabled: true,
        search: {
          type: 'CATEGORICAL',
          value: {
            values: [value],
          } as CustomFilterCategorical,
        },
      });
    } else {
      switch (dataType) {
        case DataType.STRING:
        case DataType.NUMBER:
          filterState.filter.fields.push({
            field,
            label,
            enabled: true,
            search: {
              type: 'LITERAL',
              value: {
                properties: {
                  dataType,
                },
                operator: 'eq',
                value,
              } as CustomFilterLiteralSearch,
            },
          });
          break;
        case DataType.DATE: {
          const startOfMinute: number = moment(value).startOf('minute').valueOf();
          const endOfMinute: number = moment(value).endOf('minute').valueOf();
          filterState.filter.fields.push({
            field,
            label,
            enabled: true,
            search: {
              type: 'DATE',
              value: {
                startDate: {
                  label: moment(startOfMinute).format('LLLL'),
                  value: startOfMinute,
                  type: 'Absolute',
                },
                endDate: {
                  label: moment(endOfMinute).format('LLLL'),
                  value: endOfMinute,
                  type: 'Absolute',
                },
              } as CustomFilterDateSearch,
            },
          });
          break;
        }
        default:
          return;
      }
    }

    changePageFilter(filterState);
    this.onChangeFilter(FilterUtil.parse(filterState.filter));
  };

  private prepareFilterFields = (): void => {
    const {tab} = this.state;
    const {changePageFilter} = this.props;
    const fields: CustomFilterFields[] = FilterUtil.getCustomListRecordFields(tab);
    const filterState = store.getState().page.filter;
    filterState.items = fields;
    filterState.filter.fields = [];
    filterState.filter.type = filterState.filter.type || 'ALL_MATCH';
    changePageFilter(filterState);
    this.setState({
      filter: FilterUtil.parse(filterState.filter),
      uiFilter: filterState.filter,
    });
  };

  private setBreadcrumb = (listName?: string): void => {
    const listId: string = this.props.router.params.listId ?? '';
    const pages = CustomListRouteUtil.buildListBreadcrumb(listId, this.state.tab, listName);
    dispatch(changePagePath(pages));
    dispatch(changeActivePageIndex(pages.length - 1));
  };

  private loadList = async (): Promise<void> => {
    const listId: string = this.props.router.params.listId;
    if (!listId) {
      return;
    }

    try {
      const list: CustomListModel = await this.customListService.getById(listId);
      this.setState({list});
      this.setBreadcrumb(list.name);
    } catch (e) {
      console.error(e);
      this.setBreadcrumb();
      MessageUtil.error({
        summary: LanguageUtil.get({id: 'pages.customList.detailLoadFailed', defaultMessage: 'Failed to load custom list details'}),
      });
    }
  };

  private loadRecords = async (): Promise<void> => {
    const listId: string = this.props.router.params.listId;
    if (!listId) {
      return;
    }

    const {tab, currentPage, rows, sorts, uiFilter} = this.state;
    this.setState({loading: true});
    try {
      const result = await this.customListService.searchRecords(listId, {
        recordType: this.customListService.toDbRecordType(tab),
        page: currentPage,
        size: rows,
        sort: sorts[0]
          ? {field: sorts[0].field, order: sorts[0].operator === 'ASC' ? 'ASC' : 'DESC'}
          : {field: 'createdAt', order: 'DESC'},
        filter: this.customListService.mapUiFilterToRequest(uiFilter),
      });
      this.setState({
        records: result.items || [],
        total: result.total || 0,
        loading: false,
      });
    } catch (e) {
      console.error(e);
      this.setState({loading: false, records: [], total: 0});
      MessageUtil.error({
        summary: LanguageUtil.get({id: 'pages.customList.detailLoadFailed', defaultMessage: 'Failed to load custom list details'}),
      });
    }
  };

  private redirectIfTabMissing = (): boolean => {
    const listId: string | undefined = this.props.router.params.listId;
    const recordType: string | undefined = this.props.router.params.recordType;
    if (!listId) {
      return false;
    }

    const searchParams = new URLSearchParams(this.props.router.location.search);
    const page: string = searchParams.get('page') || '1';
    const row: string = searchParams.get('row') || '10';

    if (recordType == undefined) {
      this.props.router.navigate(`/custom-list/${listId}/individual?page=${page}&row=${row}`, {replace: true});
      return true;
    }

    if (recordType !== 'individual' && recordType !== 'entity') {
      this.props.router.navigate(`/custom-list/${listId}/individual?page=${page}&row=${row}`, {replace: true});
      return true;
    }

    return false;
  };

  private parseRouteState = (): QueryState => {
    const searchParams = new URLSearchParams(this.props.router.location.search);
    return {
      tab: CustomListRouteUtil.resolveTab(this.props.router.params.recordType),
      currentPage: CustomListRouteUtil.parsePositiveInt(searchParams.get('page'), 1),
      rows: CustomListRouteUtil.parsePositiveInt(searchParams.get('row'), 10),
    };
  };

  private writePaginationParams = (): void => {
    const {tab, currentPage, rows} = this.state;
    const listId: string = this.props.router.params.listId ?? '';
    this.navigateToTabPath(listId, tab, currentPage, rows);
  };

  private navigateToTabPath = (listId: string, tab: CustomListTabType, page: number, rows: number): void => {
    const searchParams = new URLSearchParams();
    searchParams.set('page', String(page));
    searchParams.set('row', String(rows));
    this.props.router.navigate(`/custom-list/${listId}/${tab}?${searchParams.toString()}`, {replace: true});
  };
}

interface Props extends FormicaProps, ReduxProps {
}

const connector = connect(null, {changePageFilter});
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(CustomListDetailContainer));
